const DATABASE_NAME = 'vectria-studio';
const DATABASE_VERSION = 1;
const STORE_NAME = 'batch-queues';
const RECORD_VERSION = 1;

type QueueStatus = 'uploading' | 'idle' | 'processing' | 'done' | 'error';

export interface PersistableQueueItem {
  id: string;
  file: File;
  previewUrl: string;
  name: string;
  size: number;
  status: QueueStatus;
  errorMessage?: string;
  jpgPreviewUrl?: string;
  metadata?: {
    title: string;
    description?: string;
    keywords: string[];
    category: string;
    marketplace?: string;
    used_model?: string;
    provider?: string;
    job_id?: string;
    asset_id?: string;
    validation_status?: string;
    validation?: { status?: string; errors?: string[]; warnings?: string[]; keywords?: string[] };
    ai_source?: string;
    ai_credit_charged?: number;
    fallback_reason?: string;
  };
}

interface QueueRecord<T> {
  ownerKey: string;
  version: number;
  updatedAt: string;
  items: Array<Omit<T, 'previewUrl' | 'jpgPreviewUrl'>>;
}

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      database.createObjectStore(STORE_NAME, { keyPath: 'ownerKey' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Could not open the batch queue database.'));
});

const requestResult = <T>(request: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Batch queue storage request failed.'));
});

const transactionComplete = (transaction: IDBTransaction): Promise<void> => new Promise((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onerror = () => reject(transaction.error || new Error('Batch queue storage transaction failed.'));
  transaction.onabort = () => reject(transaction.error || new Error('Batch queue storage transaction was aborted.'));
});

export const queueOwnerKey = (userId?: string | null): string => (
  userId ? `user:${userId}` : 'guest'
);

export const mergeQueues = <T extends { id: string }>(primary: T[], secondary: T[]): T[] => {
  const primaryIds = new Set(primary.map(item => item.id));
  return [...primary, ...secondary.filter(item => !primaryIds.has(item.id))];
};

export const serializeQueue = <T extends PersistableQueueItem>(
  ownerKey: string,
  queue: T[],
): QueueRecord<T> => ({
  ownerKey,
  version: RECORD_VERSION,
  updatedAt: new Date().toISOString(),
  items: queue.map(({ previewUrl: _previewUrl, jpgPreviewUrl: _jpgPreviewUrl, ...item }) => item),
});

export const restoreQueueItems = <T extends PersistableQueueItem>(
  record: QueueRecord<T> | undefined,
  createPreviewUrl: (file: File) => string = file => URL.createObjectURL(file),
): T[] => {
  if (!record || record.version !== RECORD_VERSION || !Array.isArray(record.items)) return [];
  return record.items.flatMap((stored) => {
    if (!stored || typeof stored !== 'object' || !(stored.file instanceof Blob)) return [];
    const storedFile = stored.file as Blob;
    const file = storedFile instanceof File
      ? storedFile
      : new File([storedFile], stored.name || 'recovered-image', { type: storedFile.type });
    const wasInterrupted = stored.status === 'processing' || stored.status === 'uploading';
    return [{
      ...stored,
      file,
      previewUrl: createPreviewUrl(file),
      status: wasInterrupted ? 'error' : stored.status,
      // The queue UI already supplies a localized error label. Do not persist a
      // hard-coded language for a reload interruption.
      errorMessage: wasInterrupted ? undefined : stored.errorMessage,
    } as T];
  });
};

export const savePersistedQueue = async <T extends PersistableQueueItem>(
  ownerKey: string,
  queue: T[],
): Promise<void> => {
  if (!('indexedDB' in window)) return;
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(serializeQueue(ownerKey, queue));
    await transactionComplete(transaction);
  } finally {
    database.close();
  }
};

export const loadPersistedQueue = async <T extends PersistableQueueItem>(ownerKey: string): Promise<T[]> => {
  if (!('indexedDB' in window)) return [];
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const record = await requestResult(transaction.objectStore(STORE_NAME).get(ownerKey)) as QueueRecord<T> | undefined;
    return restoreQueueItems(record);
  } finally {
    database.close();
  }
};

export const clearPersistedQueue = async (ownerKey: string): Promise<void> => {
  if (!('indexedDB' in window)) return;
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(ownerKey);
    await transactionComplete(transaction);
  } finally {
    database.close();
  }
};
